import { Component, type ErrorInfo, type ReactNode } from 'react';
import type { Logger } from '@dma/shared';
import './ErrorBoundary.css';

export interface ErrorBoundaryProps {
  logger: Logger;
  fallback?: ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Global Error Boundary per docs/architecture/05-error-handling-strategy.md.
 * Catches rendering errors, logs them via the injected Logger (never a raw
 * console.log), and renders a user-friendly fallback instead of a blank page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.logger.error('Unhandled UI error caught by ErrorBoundary', {
      message: error.message,
      componentStack: errorInfo.componentStack
    });
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="dma-error-boundary" role="alert">
            <p>چیزی درست پیش نرفت. لطفاً صفحه را دوباره بارگذاری کنید.</p>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
